import React, { useState } from "react";
import { FaRegUser } from "react-icons/fa";
import { MdOutlineMailOutline, MdOutlineLocationOn } from "react-icons/md";
import { GoArrowRight } from "react-icons/go";

import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { BlackButton } from "../../components/Buttons";
import EditPassword from "../../components/user-dashboard/modal/EditPassword";
import Subscription from "../../components/user-dashboard/modal/Subscription";
import Modal from "../../components/user-dashboard/modal/Modal";
import Edit from "../../components/user-dashboard/modal/Edit";

const UserProfile = () => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [isSubscriptionOpen, setIsSubscriptionOpen] = useState(false);
  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();

  const handleEditPassword = (updatedUser) => setIsPasswordOpen(false);
  const handleEditUser = (updatedUser) => setIsEditOpen(false);
  const handleUpdateSubscription = (updatedUser) =>
    setIsSubscriptionOpen(false);

  return (
    <>
      <Helmet>
        <title>My Account</title>
        <meta name="description" content={`Welcome to My Account page`} />
      </Helmet>

      <div className="w-full px-4 sm:px-8 md:px-12 lg:px-20 py-8 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
            My Account
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Manage your profile, addresses and preferences
          </p>
        </div>

        {/* Account Info Section */}
        <div className="mb-10">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">
            Account Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Contact Card */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-r from-black to-gray-700">
                  <FaRegUser size={24} color="white" />
                </div>

                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-gray-800 mb-2">
                    Contact Information
                  </h3>

                  <p className="text-gray-600 text-sm">{user?.name}</p>
                  <p className="text-gray-600 text-sm">{user?.email}</p>
                  <p className="text-gray-600 text-sm">{user?.phone}</p>

                  <div className="flex flex-wrap gap-2 mt-4">
                    <BlackButton
                      title="Edit"
                      handleClick={() => setIsEditOpen(true)}
                      icon={<GoArrowRight />}
                    />
                    <BlackButton
                      title="Change Password"
                      handleClick={() => setIsPasswordOpen(true)}
                      icon={<GoArrowRight />}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Newsletter Card */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-r from-black to-gray-700">
                  <MdOutlineMailOutline size={24} color="white" />
                </div>

                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-gray-800 mb-2">
                    Newsletter
                  </h3>

                  <p className="text-gray-600 text-sm">
                    You aren't subscribed to our newsletter.
                  </p>

                  <div className="mt-4">
                    <BlackButton
                      title="Edit"
                      handleClick={() => setIsSubscriptionOpen(true)}
                      icon={<GoArrowRight />}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Address Section */}
        <div>
          <h2 className="text-lg font-semibold text-gray-700 mb-4">
            Address Book
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Billing */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-r from-black to-gray-700">
                  <MdOutlineLocationOn size={24} color="white" />
                </div>

                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-gray-800 mb-2">
                    Billing Address
                  </h3>

                  <p className="text-gray-600 text-sm">
                    {user?.street_address_1 ||
                      "You have not set a default billing address."}
                  </p>

                  <div className="mt-4">
                    <BlackButton
                      title="Edit Address"
                      handleClick={() => navigate("/user/address")}
                      icon={<GoArrowRight />}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Shipping */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-r from-black to-gray-700">
                  <MdOutlineLocationOn size={24} color="white" />
                </div>

                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-gray-800 mb-2">
                    Shipping Address
                  </h3>

                  <p className="text-gray-600 text-sm">
                    {user?.street_address_2 ||
                      "You have not set a default shipping address."}
                  </p>

                  <div className="mt-4">
                    <BlackButton
                      title="Edit Address"
                      handleClick={() => navigate("/user/address")}
                      icon={<GoArrowRight />}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modals (unchanged) */}
        <Modal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          title="Edit User Info"
        >
          <Edit userData={user} onSave={handleEditUser} />
        </Modal>

        <Modal
          isOpen={isPasswordOpen}
          onClose={() => setIsPasswordOpen(false)}
          title="Change Password"
        >
          <EditPassword userData={user} onSave={handleEditPassword} />
        </Modal>

        <Modal
          isOpen={isSubscriptionOpen}
          onClose={() => setIsSubscriptionOpen(false)}
          title="Update User Subscription"
        >
          <Subscription userData={user} onSave={handleUpdateSubscription} />
        </Modal>
      </div>
    </>
  );
};

export default UserProfile;
